import type { ActionResult, OrderBulkAction, OrderBulkActionResult, OrderImportBody, OrderImportResult } from '@flux/shared';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import {
  bulkOrderAction,
  ignoreOrder,
  importOrder,
  markOrderHandled,
  reactivateOrder,
  refreshOrder,
  regenerateOrder,
  sendOrderNow,
} from './api.js';

/**
 * Actions manuelles de `01` §4 (`02` §3, §4). Chaque mutation :
 * - affiche le message exact renvoyé par le serveur au succès (`02` §0),
 *   ou `ApiError.message` à l'échec — y compris un 409 (transition de statut
 *   invalide, doublon possible…), déjà en français courant (`04` §6) ;
 * - invalide `queryKeys.orders.all` (fabrique centrale, `lib/query/keys.ts`)
 *   pour rafraîchir à la fois la liste/le détail de cette vague et le badge
 *   « Commandes » de la barre latérale (`use-attention-count.ts`).
 */
function useOrderActionMutation(
  action: (id: string) => Promise<ActionResult>,
): UseMutationResult<ActionResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: (result) => {
      toast.success(result.message ?? 'Action effectuée.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/** « Envoyer maintenant » — `01` §4 : disponible depuis `scheduled`. */
export function useSendOrderNow(): UseMutationResult<ActionResult, unknown, string> {
  return useOrderActionMutation(sendOrderNow);
}

/** « Mettre à jour depuis Shopify » — `01` §4 : partout sauf en préparation ; le toast dit ce que donnerait un renvoi. */
export function useRefreshOrder(): UseMutationResult<ActionResult, unknown, string> {
  return useOrderActionMutation(refreshOrder);
}

/**
 * « Renvoyer la commande à Martelet » (ex « Régénérer ») — `01` §4 : relecture
 * Shopify, nouveau fichier et envoi ; disponible depuis `error`, `blocked`,
 * `handled`, `ignored`, `sent`, `integrated`, `prepared`, `shadow`.
 */
export function useRegenerateOrder(): UseMutationResult<ActionResult, unknown, string> {
  return useOrderActionMutation(regenerateOrder);
}

/** « Réactiver » — `01` §4 : retour en `scheduled` avec envoi immédiat, disponible depuis `ignored`. */
export function useReactivateOrder(): UseMutationResult<ActionResult, unknown, string> {
  return useOrderActionMutation(reactivateOrder);
}

/** « Marquer traitée » — `01` §4 : disponible depuis `blocked`. */
export function useMarkOrderHandled(): UseMutationResult<ActionResult, unknown, string> {
  return useOrderActionMutation(markOrderHandled);
}

/** « Marquer ignorée » — `01` §4 : motif obligatoire, disponible depuis `scheduled`, `error`, `blocked`. */
export function useIgnoreOrder(): UseMutationResult<ActionResult, unknown, { id: string; reason: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => ignoreOrder(id, reason),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Commande marquée ignorée.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/** « Importer une commande » (`02` §3) : crée la commande si inconnue, ou renvoie l'existante. */
export function useImportOrder(): UseMutationResult<OrderImportResult, unknown, OrderImportBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: importOrder,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

export interface BulkActionVariables {
  kind: OrderBulkAction;
  orderIds: readonly string[];
  reason?: string | undefined;
}

/**
 * Sélection multiple (`02` §3) : chaque commande réussit ou échoue
 * indépendamment (`OrderBulkActionResult`), jamais de rollback global —
 * l'appelant (`bulk-action-bar.tsx`) affiche le détail des échecs.
 */
export function useBulkOrderAction(): UseMutationResult<OrderBulkActionResult, unknown, BulkActionVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, orderIds, reason }: BulkActionVariables) => bulkOrderAction(kind, orderIds, reason),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      if (result.failed.length === 0) {
        toast.success(
          `${String(result.succeeded.length)} commande${result.succeeded.length > 1 ? 's' : ''} traitée${result.succeeded.length > 1 ? 's' : ''}.`,
        );
      } else if (result.succeeded.length === 0) {
        toast.error(`Échec pour les ${String(result.failed.length)} commande(s) sélectionnée(s).`);
      } else {
        toast.error(
          `${String(result.succeeded.length)} commande(s) traitée(s), ${String(result.failed.length)} en échec.`,
        );
      }
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}
